import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Users, Building2, ScrollText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

/**
 * One-screen overview of the platform. Counts come from `head: true,
 * count: 'exact'` queries — Supabase returns just the count and no rows,
 * which is cheap even on the largest tables. RLS only lets a super admin
 * see across workspaces; a non-super-admin would never reach this page
 * because RequireSuperAdmin redirects them to /app/dashboard.
 */

type CountResult = number | null;

async function fetchCount(table: 'users' | 'workspaces' | 'super_admin_audit'): Promise<CountResult> {
  // `super_admin_audit` is missing from the generated Supabase types until
  // Sergio regenerates them after applying migration 20260513170000; cast
  // through unknown so all three table names share a single code path.
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (
        cols: string,
        opts: { count: 'exact'; head: true },
      ) => Promise<{ count: number | null; error: unknown }>;
    };
  };
  const { count, error } = await client
    .from(table)
    .select('*', { count: 'exact', head: true });
  if (error) {
    console.error(`SuperAdminDashboard: failed to count ${table}`, error);
    return null;
  }
  return count ?? 0;
}

interface AuditRowSummary {
  id: string;
  action: string;
  target_table: string | null;
  created_at: string;
}

async function fetchRecentAudit(): Promise<AuditRowSummary[]> {
  // The generated Supabase types are regenerated post-migration; cast through
  // unknown so this compiles before Sergio re-runs `generate types`.
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (cols: string) => {
        order: (col: string, opts: { ascending: boolean }) => {
          limit: (n: number) => Promise<{ data: AuditRowSummary[] | null; error: unknown }>;
        };
      };
    };
  };
  const { data, error } = await client
    .from('super_admin_audit')
    .select('id, action, target_table, created_at')
    .order('created_at', { ascending: false })
    .limit(5);
  if (error) {
    console.error('SuperAdminDashboard: failed to load recent audit rows', error);
    return [];
  }
  return data ?? [];
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export const SuperAdminDashboard: React.FC = () => {
  const workspacesQuery = useQuery({
    queryKey: ['superadmin', 'count', 'workspaces'],
    queryFn: () => fetchCount('workspaces'),
  });
  const usersQuery = useQuery({
    queryKey: ['superadmin', 'count', 'users'],
    queryFn: () => fetchCount('users'),
  });
  const auditCountQuery = useQuery({
    queryKey: ['superadmin', 'count', 'super_admin_audit'],
    queryFn: () => fetchCount('super_admin_audit'),
  });
  const recentAuditQuery = useQuery({
    queryKey: ['superadmin', 'audit', 'recent'],
    queryFn: fetchRecentAudit,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Panel super admin</h1>
        <p className="text-sm text-gray-500 mt-1">
          Vista global de la plataforma. Solo visible para el super admin.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          label="Workspaces"
          icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
          value={workspacesQuery.data}
          loading={workspacesQuery.isLoading}
        />
        <MetricCard
          label="Usuarios"
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          value={usersQuery.data}
          loading={usersQuery.isLoading}
        />
        <MetricCard
          label="Acciones en auditoría"
          icon={<ScrollText className="h-4 w-4 text-muted-foreground" />}
          value={auditCountQuery.data}
          loading={auditCountQuery.isLoading}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Últimas acciones</CardTitle>
        </CardHeader>
        <CardContent>
          {recentAuditQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (recentAuditQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-gray-500 py-4">Aún no hay acciones registradas.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {(recentAuditQuery.data ?? []).map((row) => (
                <li key={row.id} className="py-2 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{row.action}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {row.target_table ?? 'sin tabla objetivo'}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatTimestamp(row.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface MetricCardProps {
  label: string;
  icon: React.ReactNode;
  value: number | null | undefined;
  loading: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, icon, value, loading }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{label}</CardTitle>
      {icon}
    </CardHeader>
    <CardContent>
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      ) : (
        <div className="text-2xl font-bold">{value ?? '—'}</div>
      )}
    </CardContent>
  </Card>
);
